// Graph.tsx
import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { formatNumber } from "../../module/map/analysis";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  // Tooltip,
  Legend,
  ChartDataLabels
);

interface GraphProps {
  title: string;
  labels: string[];
  values: number[];
  colors?: string[];
}

const Graph: React.FC<GraphProps> = ({ title, labels, values, colors }) => {
  const fallback = '#015EFF';

  const bgColors =
    colors && colors.length === values.length
      ? colors
      : Array(values.length).fill(fallback);

  const totalCount = values.reduce((sum, v) => sum + v, 0);
  // 25.07.09 | Kim Young Jun | 1000 단위 구분자 포매팅
  const formattedTotalCount = formatNumber(totalCount);

  const data: ChartData<"bar"> = {
    labels,
    datasets: [
      {
        label: title,
        data: values,
        backgroundColor: bgColors,
        borderRadius: 4,
        datalabels: {
          anchor: "end",
          align: "top",
          color: "#004DCC",
          font: {
            size: 9,
            weight: "bold",
          },
          padding: { top: 8 },
          textStrokeColor: "#FFFFFF",
          textStrokeWidth: 3,
          formatter: (value: number) => formatNumber(value)
        },
      },
    ],
  };

  const titleOutlinePlugin = {
    id: 'titleOutline',
    beforeDraw(chart: ChartJS) {
      const { ctx } = chart;
      const titleOpts = chart.options.plugins?.title;
      if (titleOpts && titleOpts.display && typeof titleOpts.text === 'string') {
        const text = titleOpts.text;
        const x = chart.width / 2;
        const y = chart.height - 35;

        ctx.save();
        ctx.font = "bold 12px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        // 외곽선 (stroke)
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#FFFFFF"; 
        ctx.strokeText(text, x, y);

        // 텍스트 (fill)
        ctx.fillStyle = "#004DCC"; 
        ctx.fillText(text, x, y);

        ctx.restore();
      }
    }
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 40,
        right: 20,
        bottom: 10,
        left: 20,
      },
    },
    plugins: {
      title: {
        display: true,
        text: `전체 ${formattedTotalCount}개`,
        color: "rgba(0,0,0,0)",
        position: "bottom",
        font: {
          size: 9,
          weight: "bold",
        },
        align: "center",
      },
      legend: {
        display: false,
      },
      // datalabels: {
      //   anchor: "end",
      //   align: "top",
      //   color: "#FFFFFF",
      //   font: { weight: "bold" },
      // },
    },
    scales: {
      y: {
        display: true,
        beginAtZero: true,
        ticks: { display: false },
        grid: {
          lineWidth: (ctx) => (ctx.tick?.value === 0 ? 2 : 1),
          color: (ctx) => (ctx.tick?.value === 0 ? "#000" : "rgba(0,0,0,0.15)"),
          drawTicks: false,
        }
      },
      x: {
        grid: { display: false, drawTicks: false },
        ticks: { 
          display: true,
          color: "#004DCC",
          font: {
            size: 9,
            weight: "bold",
          },
          padding: 10,
          textStrokeColor: "#FFFFFF",
          textStrokeWidth: 3,
        },
      },
    },
  };

  return (
    <>
      {labels.length > 0 && values.length > 0 ? (
        <Bar data={data} options={options} plugins={[titleOutlinePlugin]} />
      ) : (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          height: '100%', color: '#004DCC', fontWeight: 'bold', fontSize: '16px'
        }}>
          장소정보가 없습니다.
        </div>
      )}
    </>
  );
};

export default Graph;
